import { ReadFields } from 'models/enum';
import {
  IVideo,
  IProfileCompletion,
  IEmployee,
  ISkill,
  ILocation,
  AnalizedResumeStatus,
  IGlobalStore,
  IEmployeeProfileFlags,
  ICompetency,
  ISuccessProfile,
  ITopCategories,
} from 'modules/profile/models/IProfile.interface';

export interface IEmployeeState {
  employee: IEmployee | null;
  employeeProfileFlags: IEmployeeProfileFlags;
  error: string;
  isFetchingData: boolean;
  isLoadingProfileCompleteness: boolean;
}

export enum UploadResumeSteps {
  uploadFile = 'UPLOAD_FILE',
  selectSections = 'SELECT_SECTIONS',
  summary = 'SUMMARY',
}

export interface IProfileCompletionError {
  message?: string;
  section: 'workExperience' | 'skills' | 'degreesAndCertifications';
}

export interface IProfileCompletionState {
  resumeModalState: {
    opened: boolean;
    currentStep: UploadResumeSteps;
  };
  profileCompletion: IProfileCompletion;
  error: string | IProfileCompletionError;
  isFetchingData: boolean;
}

export interface IMarkedAsReadFieldsState {
  store: IGlobalStore;
  isFetchingData: boolean;
}

export const PROFILE_COMPLETION_INITIAL_STATE: IProfileCompletionState = {
  resumeModalState: {
    opened: false,
    currentStep: UploadResumeSteps.uploadFile,
  },
  profileCompletion: {
    analysisStatus: AnalizedResumeStatus.idle,
    analized: {
      workExperiences: [],
      skills: [],
      degreesAndCertifications: [],
    },
    selected: [],
    unnormSkills: [],
  },
  error: '',
  isFetchingData: false,
};

export const EMPLOYEE_INITIAL_STATE: IEmployeeState = {
  employee: null,
  employeeProfileFlags: {
    prepolulateInterestedRoles: false,
    forceOpenToWork: false,
    assessmentFlags: {
      tellUsAboutYourself: {
        isSkipped: false,
      },
    },
  },
  error: '',
  isFetchingData: false,
  isLoadingProfileCompleteness: false,
};

export interface IVideoState {
  video: IVideo | null;
  error: string;
  isFetchingData: boolean;
}

export const RECOMMENDED_VIDEO_INITIAL_STATE: IVideoState = {
  video: null,
  error: '',
  isFetchingData: false,
};

export interface IManageSkillsState {
  suggestedSkills: ISkill[] | null;
  suggestedCompetencies: ICompetency[] | null;
  selectedSkills: ISkill[] | null;
  skills: ISkill[] | null;
  foundSkills: ISkill[] | null;
  error: string;
  isFetchingData: boolean;
  isFetchingOperation: boolean;
}

export interface IManageCompetenciesState {
  suggestedCompetencies: ICompetency[] | null;
  error: string;
  isFetchingData: boolean;
  allCompetencies: ICompetency[] | null;
  isFetchingAllData: boolean;
}

export interface ICareerPreferencesState {
  careerModalState: {
    opened: boolean;
  };
  locations: ILocation[];
  error: string;
  isFetchingData: boolean;
  openToWorkToggle: boolean;
}

export const MANAGE_SKILLS_INITIAL_STATE: IManageSkillsState = {
  suggestedSkills: [],
  suggestedCompetencies: [],
  selectedSkills: [],
  skills: [],
  foundSkills: [],
  error: '',
  isFetchingData: false,
  isFetchingOperation: false,
};

export const MANAGE_COMPETENCIES_INITIAL_STATE: IManageCompetenciesState = {
  suggestedCompetencies: [],
  error: '',
  isFetchingData: false,
  allCompetencies: [],
  isFetchingAllData: false,
};

export const CAREER_PREFERENCES_INITIAL_STATE: ICareerPreferencesState = {
  careerModalState: {
    opened: false,
  },
  locations: [],
  error: '',
  isFetchingData: false,
  openToWorkToggle: false,
};

export interface IWorkHistoryState {
  employee: IEmployee | null;
  error: string;
  isFetchingData: boolean;
  workHistoryModalState: {
    opened: boolean;
  };
}

export const WORK_HISTORY_INITIAL_STATE: IWorkHistoryState = {
  employee: null,
  error: '',
  isFetchingData: false,
  workHistoryModalState: {
    opened: false,
  },
};

export interface IEducationState {
  educationModalState: {
    opened: boolean;
  };
  employee: IEmployee | null;
  error: string;
  isFetchingData: boolean;
}

export const EDUCATION_INITIAL_STATE: IEducationState = {
  educationModalState: {
    opened: false,
  },
  employee: null,
  error: '',
  isFetchingData: false,
};

export interface ISuccessProfileTopCategoriesState {
  topCategories: ITopCategories | null;
  error: string;
  isFetchingData: boolean;
}

export const SUCCESS_PROFILE_TOP_CATEGORIES_INITIAL_STATE: ISuccessProfileTopCategoriesState =
  {
    topCategories: null,
    error: '',
    isFetchingData: false,
  };

export interface ISuccessProfileState {
  successProfile: ISuccessProfile | null;
  error: string;
  isFetchingData: boolean;
}

export const SUCCESS_PROFILE_INITIAL_STATE: ISuccessProfileState = {
  successProfile: null,
  error: '',
  isFetchingData: false,
};

export const MARKED_FIELDS_INITIAL_STATE: IMarkedAsReadFieldsState = {
  store: {
    id: 0,
    orgId: 0,
    [ReadFields.SuggestedSkills]: false,
    [ReadFields.CareerPreferences]: false,
    [ReadFields.MySkills]: false,
    [ReadFields.InterestedRoles]: false,
    [ReadFields.AssessmentCompleted]: false,
    [ReadFields.DegreesAndCertificationsCompleted]: false,
    [ReadFields.NextBestRoleCompleted]: false,
    [ReadFields.ResumeCompleted]: '',
    [ReadFields.SkillsCompleted]: false,
    [ReadFields.WorkExperienceCompleted]: false,
    [ReadFields.CareerProgressionViewed]: false,
  },
  isFetchingData: false,
};

export interface IOnboardEmployeeSkillsState {
  isUpdatingSkills: boolean;
  waitForNotification: boolean;
  notificationArrived: boolean;
}

export const ONBOARD_EMPLOYEE_SKILLS_INITIAL_STATE: IOnboardEmployeeSkillsState =
  {
    isUpdatingSkills: false,
    waitForNotification: false,
    notificationArrived: false,
  };

export interface IOnboardEmployeeRolesState {
  isUpdatingRoles: boolean;
  waitForNotification: boolean;
  notificationArrived: boolean;
}

export const ONBOARD_EMPLOYEE_ROLES_INITIAL_STATE: IOnboardEmployeeRolesState =
  {
    isUpdatingRoles: false,
    waitForNotification: false,
    notificationArrived: false,
  };

export interface IOnboardEmployeeOpenToWorkState {
  isUpdatingOpenToWork: boolean;
  waitForNotification: boolean;
  notificationArrived: boolean;
}

export const ONBOARD_EMPLOYEE_OPEN_TO_WORK_INITIAL_STATE: IOnboardEmployeeOpenToWorkState =
  {
    isUpdatingOpenToWork: false,
    waitForNotification: false,
    notificationArrived: false,
  };
